import React, { useEffect, useState, useRef } from "react";
import MasterLayoutGames from "../layouts/GamesLayout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ICON PATHS
const ICON_PATHS = [
  "/games/memory/mi1.svg",
  "/games/memory/mi2.svg",
  "/games/memory/mi3.svg",
  "/games/memory/mi4.svg",
  "/games/memory/mi5.svg",
  "/games/memory/mi6.svg",
  "/games/memory/mi7.svg",
  "/games/memory/mi8.svg",
  "/games/memory/mi9.svg",
  "/games/memory/mi10.svg",
  "/games/memory/mi11.svg",
  "/games/memory/mi12.svg",
];

// CARD BACK OPTIONS
const CARD_BACKS = [
  "/games/memory/memory-card-back.jpg",
  "/games/memory/memory-card-back2.jpg",
  "/games/memory/memory-card-back3.jpg",
  "/games/memory/memory-card-back4.jpg",
];

export default function MemoryGamePage() {
  const [grid, setGrid] = useState({ rows: 3, cols: 4 });
  const [cards, setCards] = useState([]);
  const [opened, setOpened] = useState([]);
  const [matched, setMatched] = useState([]);
  const [moves, setMoves] = useState(0);
  const [soundOn, setSoundOn] = useState(true);

  const [cardBack, setCardBack] = useState(""); // 🔑 1 back per game

  const matchAudioRef = useRef(null);
  const winAudioRef = useRef(null);

  useEffect(() => {
    matchAudioRef.current = new Audio("/sounds/bell.mp3");
    winAudioRef.current = new Audio("/sounds/win-bell.mp3");

    matchAudioRef.current.volume = 0.4;
    winAudioRef.current.volume = 0.6;

    initGame();
  }, [grid]);

  const initGame = () => {
    const total = grid.rows * grid.cols;
    const needed = total / 2;

    // 🎴 pick 1 random back for whole game
    const back =
      CARD_BACKS[Math.floor(Math.random() * CARD_BACKS.length)];
    setCardBack(back);

    const picked = shuffle([...ICON_PATHS]).slice(0, needed);

    const deck = shuffle(
      [...picked, ...picked].map((icon, i) => ({
        id: i,
        icon,
      }))
    );

    setCards(deck);
    setOpened([]);
    setMatched([]);
    setMoves(0);
  };

  const handleClick = (card) => {
    if (opened.length === 2) return;
    if (opened.includes(card.id)) return;
    if (matched.includes(card.id)) return;

    const newOpened = [...opened, card.id];
    setOpened(newOpened);

    if (newOpened.length === 2) {
      setMoves((m) => m + 1);

      const [a, b] = newOpened.map((id) =>
        cards.find((c) => c.id === id)
      );

      if (a.icon === b.icon) {
        const newMatched = [...matched, a.id, b.id];
        setMatched(newMatched);

        if (soundOn && matchAudioRef.current) {
          matchAudioRef.current.currentTime = 0;
          matchAudioRef.current.play();
        }

        if (newMatched.length === cards.length) {
          setTimeout(() => {
            if (soundOn && winAudioRef.current) {
              winAudioRef.current.currentTime = 0;
              winAudioRef.current.play();
            }
          }, 1000);
        }

        setOpened([]);
      } else {
        setTimeout(() => setOpened([]), 1000); // sedikit lebih smooth
      }
    }
  };

  const isOpen = (id) => opened.includes(id) || matched.includes(id);
  const isMatched = (id) => matched.includes(id);

  return (
    <MasterLayoutGames title="Memory Spiritual">
      {/* TOP BAR */}
      <div style={styles.topBar}>
        <div style={styles.score}>Moves: {moves}</div>

        <div style={{ display: "flex", gap: "0.5rem" }}>
          <button
            style={styles.soundBtn}
            onClick={() => setSoundOn(!soundOn)}
          >
            {soundOn ? "🔊" : "🔇"}
          </button>

          <button style={styles.restartBtn} onClick={initGame}>
            Restart
          </button>
        </div>
      </div>

      {/* CONFIG */}
      <div style={styles.config}>
        <button onClick={() => setGrid({ rows: 3, cols: 4 })}>
          3 x 4
        </button>
        <button onClick={() => setGrid({ rows: 4, cols: 5 })}>
          4 x 5
        </button>
      </div>

      {/* BOARD */}
      <div
        style={{
          ...styles.grid,
          gridTemplateColumns: `repeat(${grid.cols}, 1fr)`,
        }}
      >
        {cards.map((card) => {
          return (
            <div
              key={card.id}
              style={styles.card}
              onClick={() => handleClick(card)}
            >
              <div
                style={{
                  ...styles.inner,
                  transform: isOpen(card.id)
                    ? "rotateY(180deg) scale(1.02)"
                    : "rotateY(0deg) scale(1)",
                }}
              >
                {/* FRONT */}
                <div
                  style={{
                    ...styles.front,
                    backgroundImage: `url(${cardBack})`,
                  }}
                />

                {/* BACK */}
                <div
                  style={{
                    ...styles.back,
                    ...(isMatched(card.id) ? styles.matchGlow : {}),
                  }}
                >
                  <img
                    src={card.icon}
                    alt=""
                    style={styles.icon}
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </MasterLayoutGames>
  );
}

// utils
function shuffle(arr) {
  return arr.sort(() => Math.random() - 0.5);
}

// STYLES
const styles = {
  topBar: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "1rem",
  },
  score: {
    fontSize: "1.3rem",
    fontWeight: "600",
  },
  restartBtn: {
    fontSize: "1.1rem",
    padding: "0.6rem 1.2rem",
    borderRadius: "1rem",
    border: "none",
    background: "#7cc6a6",
    color: "#fff",
    cursor: "pointer",
  },
  soundBtn: {
    fontSize: "1.2rem",
    padding: "0.5rem 0.8rem",
    borderRadius: "1rem",
    border: "none",
    background: "#ffd6e0",
    cursor: "pointer",
  },
  config: {
    display: "flex",
    gap: "0.5rem",
    marginBottom: "1rem",
  },
  grid: {
    display: "grid",
    gap: "0.9rem",
  },
  card: {
    width: "100%",
    paddingTop: "100%",
    position: "relative",
    perspective: "1000px",
    cursor: "pointer",
  },
  inner: {
    position: "absolute",
    inset: 0,
    transition: "transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)", // 🔥 smooth
    transformStyle: "preserve-3d",
  },
  front: {
    position: "absolute",
    inset: 0,
    borderRadius: "1.4rem",
    backgroundSize: "cover",
    backgroundPosition: "center",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    backfaceVisibility: "hidden",
  },
  back: {
    position: "absolute",
    inset: 0,
    borderRadius: "1.4rem",
    background: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transform: "rotateY(180deg)",
    backfaceVisibility: "hidden",
  },
  icon: {
    width: "65%",
    height: "65%",
    objectFit: "contain",
  },
  matchGlow: {
    boxShadow: "0 0 10px #7CFC9A, 0 0 20px #7CFC9A",
    transform: "scale(1.05)",
  },
};